import React, { useState, useEffect } from "react";
import "./Filter.css";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { IoIosArrowDown } from "react-icons/io";
import { BiSearch } from "react-icons/bi";
import Slider from "@mui/material/Slider";

const Filter = ({
  selectedCategory,
  selectedMetals,
  selectedSizes,
  priceRange,
  selectedBrands,
  onFilterChange
}) => {
  // Local state for search functionality
  const [searchTerm, setSearchTerm] = useState("");
  const [availableSizes, setAvailableSizes] = useState([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState("10k");
  
  const priceRanges = {
    "10k": { min: 1, max: 10000 },
    "30k": { min: 1, max: 30000 },
    "100k": { min: 1, max: 100000 },
    "1m": { min: 1, max: 1000000 }
  };

  // Initial data
  const [brandsData] = useState([
    { name: "Hussain Sons", count: 60 },
    { name: "Adidas", count: 2 },
    { name: "Balmain", count: 7 },
    { name: "Balenciaga", count: 10 },
    { name: "Burberry", count: 39 },
    { name: "Kenzo", count: 95 },
    { name: "Givenchy", count: 1092 },
    { name: "Zara", count: 48 },
  ]);

  const filterCategories = [
    "All",
    "Rings",
    "Chains",
    "Necklace",
    "Bangles",
    "Bracelet",
    "Earrings & Tops",
    "Jhumke",
    "Anklet",
    "Pendants",
    "Engagement Rings",
    "Bridal Sets",
    "Men - Rings",
  ];

  const filterMetals = [
    { label: "Gold", color: "#f7e7b2" },
    { label: "White Gold", color: "#E5E4E2" },
    { label: "Rose Gold", color: "#e6c7c2" },
    { label: "925 Silver", color: "#C0C0C0" },
    { label: "Platinum", color: "#E5E4E2" },
  ];

  const sizeOptionsByCategory = {
    "Necklace": ["Collar", "Choker", "Princess", "Matinee", "Opera", "Rope", "Lariat"],
    "Rings": ["Solitaire", "Halo", "Tension", "Signet", "Eternity"],
    "Men - Rings": ["Band", "Signet", "Solitaire"],
    // Add more categories as needed
  };


  // In Filter.jsx
  const handleCategoryClick = (category) => {
    console.log("Selected category:", category);  // Add this
    console.log("Available sizes for category:", sizeOptionsByCategory[category]);
    onFilterChange.category(category);
  };

  useEffect(() => {
    // Clear selected sizes when category changes
    onFilterChange.sizes([]);
    
    // Set available sizes based on selected category
    if (selectedCategory in sizeOptionsByCategory) {
      setAvailableSizes(sizeOptionsByCategory[selectedCategory]);
    } else {
      // Default empty array for categories without specific sizes
      setAvailableSizes([]);
    }
  }, [selectedCategory]);

  const handleMetalChange = (metal) => {
    const updatedMetals = selectedMetals.includes(metal)
      ? selectedMetals.filter(m => m !== metal)
      : [...selectedMetals, metal];
      console.log("Selected metals:", updatedMetals); // Add this line
    onFilterChange.metals(updatedMetals);
  };

  const handleSizeChange = (size) => {
    const updatedSizes = selectedSizes.includes(size)
      ? selectedSizes.filter(s => s !== size)
      : [...selectedSizes, size];
    onFilterChange.sizes(updatedSizes);
  };

  const handlePriceRangeTypeChange = (range) => {
    setSelectedPriceRange(range);
    // Reset the price range to min-max of new range
    onFilterChange.price([priceRanges[range].min, priceRanges[range].max]);
  };
  
  const handlePriceChange = (event, newValue) => {
    onFilterChange.price(newValue);
  };

  const handleBrandChange = (brandName) => {
    const updatedBrands = selectedBrands.includes(brandName)
      ? selectedBrands.filter(b => b !== brandName)
      : [...selectedBrands, brandName];
    onFilterChange.brands(updatedBrands);
  };

  // Filter brands based on search term
  const filteredBrands = brandsData.filter((brand) =>
    brand.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="filterSection">
        {/* Categories Section */}
        <div className="filterCategories">
          <Accordion defaultExpanded disableGutters elevation={0}>
            <AccordionSummary
              expandIcon={<IoIosArrowDown size={20} />}
              aria-controls="panel1-content"
              id="panel1-header"
              sx={{ padding: 0, marginBottom: 2 }}
            >
              <h5 className="filterHeading">Product Categories</h5>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              {filterCategories.map((category, index) => (
                <p
                  key={index}
                  onClick={() => handleCategoryClick(category)}
                  style={{
                    color: selectedCategory === category ? '#aeaeae' : '#000000',
                    cursor: 'pointer',
                    padding: '4px 0',
                    margin: 0,
                    transition: 'color 0.2s ease',
                    fontWeight: selectedCategory === category ? '600' : '400'
                  }}
                >
                  {category}
                </p>
              ))}
            </AccordionDetails>
          </Accordion>
        </div>

        {/* Metals Section */}
        <div className="filterColors">
          <Accordion defaultExpanded disableGutters elevation={0}>
            <AccordionSummary
              expandIcon={<IoIosArrowDown size={20} />}
              aria-controls="panel2-content"
              id="panel2-header"
              sx={{ padding: 0, marginBottom: 2 }}
            >
              <h5 className="filterHeading">Metal</h5>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <div className="filterColorBtn">
                {filterMetals.map((metal, index) => (
                  <div key={index} className="metalOption">
                    <button
                      className={`colorButton ${
                        selectedMetals.includes(metal.label) ? "selected" : ""
                      }`}
                      style={{
                        backgroundColor: metal.color,
                        border: selectedMetals.includes(metal.label)
                          ? "2px solid #000"
                          : "none"
                      }}
                      onClick={() => handleMetalChange(metal.label)}
                    />
                    <span>{metal.label}</span>
                  </div>
                ))}
              </div>
            </AccordionDetails>
          </Accordion>
        </div>

        {/* Sizes Section */}
        {availableSizes.length > 0 && (
          <div className="filterSizes">
          <Accordion defaultExpanded disableGutters elevation={0}>
            <AccordionSummary
              expandIcon={<IoIosArrowDown size={20} />}
              aria-controls="panel3-content"
              id="panel3-header"
              sx={{ padding: 0, marginBottom: 2 }}
            >
              <h5 className="filterHeading">
                Sizes | Types ({availableSizes.length})
              </h5>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <div className="sizeButtons">
                {console.log("Rendering size buttons:", availableSizes)}
                {availableSizes.map((size, index) => {
                  console.log("Rendering size button:", size);
                  return (
                    <button
                      key={index}
                      className={`sizeButton ${
                        selectedSizes.includes(size) ? "selected" : ""
                      }`}
                      onClick={() => handleSizeChange(size)}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
            </AccordionDetails>
          </Accordion>
        </div>
        )}

        {/* Brands Section */}
        <div className="filterBrands">
          <Accordion defaultExpanded disableGutters elevation={0}>
            <AccordionSummary
              expandIcon={<IoIosArrowDown size={20} />}
              aria-controls="panel4-content"
              id="panel4-header"
              sx={{ padding: 0, marginBottom: 2 }}
            >
              <h5 className="filterHeading">Brands</h5>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <div className="searchBar">
                <BiSearch className="searchIcon" size={20} color={"#767676"} />
                <input
                  type="text"
                  placeholder="Search brands"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="brandList">
              {filteredBrands.length > 0 ? (
                filteredBrands.map((brand, index) => (
                  <div className="brandItem" key={index}>
                    <input
                      type="checkbox"
                      id={`brand-${index}`}
                      checked={selectedBrands.includes(brand.name)}
                      onChange={() => handleBrandChange(brand.name)}
                      className="brandRadio"
                    />
                    <label htmlFor={`brand-${index}`} className="brandLabel">
                      {brand.name}
                    </label>
                    <span className="brandCount">({brand.count})</span>
                  </div>
                ))
              ) : (
                <div className="notFoundMessage">No brands found</div>
              )}
            </div>
              </AccordionDetails>
            </Accordion>
          </div>

        {/* Price Range Section */}
        <div className="filterPrice">
          <Accordion defaultExpanded disableGutters elevation={0}>
            <AccordionSummary
              expandIcon={<IoIosArrowDown size={20} />}
              aria-controls="panel5-content"
              id="panel5-header"
              sx={{ padding: 0, marginBottom: 2 }}
            >
              <h5 className="filterHeading">Price Range</h5>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <div className="priceRangeSelector" style={{ marginBottom: "1rem" }}>
                <select 
                  value={selectedPriceRange}
                  onChange={(e) => handlePriceRangeTypeChange(e.target.value)}
                  className="rangeSelect"
                  style={{
                    padding: "0.5rem",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    width: "100%",
                    marginBottom: "1rem"
                  }}
                >
                  <option value="10k">$1 - $10,000</option>
                  <option value="30k">$1 - $30,000</option>
                  <option value="100k">$1 - $100,000</option>
                  <option value="1m">$1 - $1,000,000</option>
                </select>
              </div>
              
              <Slider
                value={priceRange}
                onChange={handlePriceChange}
                valueLabelDisplay="auto"
                min={priceRanges[selectedPriceRange].min}
                max={priceRanges[selectedPriceRange].max}
                valueLabelFormat={(value) => `$${value.toLocaleString()}`}
                sx={{
                  color: "black",
                  "& .MuiSlider-thumb": {
                    backgroundColor: "white",
                    border: "2px solid black",
                    width: 18,
                    height: 18,
                  },
                }}
              />
              <div className="filterSliderPrice">
                <div className="priceRange">
                  <p>
                    Min Price: <span>${priceRange[0].toLocaleString()}</span>
                  </p>
                  <p>
                    Max Price: <span>${priceRange[1].toLocaleString()}</span>
                  </p>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
        </div>
      </div>
    </div>
  );
};

export default Filter;